// Alias keeps Emotion 10 even when yarn hoists Emotion 11 (@emotion/react / react-select)
// to node_modules/@emotion/cache — createCache from v11 requires `key` and white-screens.
import createCache from '@emotion/cache-v10';
import { CacheProvider } from '@emotion/core';
import { PropsWithChildren } from 'react';
import { StyleSheetManager } from 'styled-components';

/**
 * Pins the stylesheet order of the two CSS-in-JS engines, which otherwise
 * depends on whichever engine happens to render first at boot: Emotion
 * (reflexbox's Box/Flex base rules) injects into a container that precedes
 * the styled-components target in <head>, so styled-components always wins
 * specificity ties (e.g. the many `styled(Box|Flex)` overrides).
 *
 * The `@emotion/core` / `@emotion/cache-v10` dependencies are intentionally
 * pinned to Emotion 10 to match reflexbox's `@emotion/core` dependency:
 * `CacheProvider` only takes effect when it is the exact module instance
 * reflexbox renders with, and this breaks *silently* (styles fall back to
 * unordered default injection) if a second copy ever appears. Import
 * createCache via the `@emotion/cache-v10` alias (not bare `@emotion/cache`)
 * so yarn's Emotion 11 hoist for react-select cannot steal this import.
 * Verify with `yarn why` on the emotion packages after any dependency change.
 */
const emotionContainer = document.createElement('div');
emotionContainer.setAttribute('data-emotion-container', '');

const styledComponentsTarget = document.createElement('div');
styledComponentsTarget.setAttribute('data-styled-target', '');

document.head.append(emotionContainer, styledComponentsTarget);

const emotionCache = createCache({ container: emotionContainer });

export function StyleInjectionProvider({ children }: PropsWithChildren) {
	return (
		<CacheProvider value={emotionCache}>
			<StyleSheetManager target={styledComponentsTarget}>{children}</StyleSheetManager>
		</CacheProvider>
	);
}
